import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import type { ExperienceConfiguration, NavigationBody } from '@ayumia/contracts';
import { text } from '../app/i18n';
import { formatRoute, sameRoute, type Route } from '../app/router';
import { Icon } from '../design-system/icons';
import { OfflineBanner } from '../states/States';
import { badgeQueries, badgeTotal, baseRoute, labelOf, routeOfItem, trailTo, type NavItem } from './navigation-model';
import { useBadges } from './useBadges';
import { Topbar, TopbarSidePanel, type TopbarPanel } from './Topbar';
import { readDensity, writeDensity, type Density } from './density-preference';
import { Monogram } from '../design-system/brand';

/**
 * AppShell (59 §4.1-§4.4, ADR-092) : barre du haut sombre, menu en **rail + tiroir** (défaut) ou en
 * arbre (`presentation.layout: tree`), fil d'Ariane, zone de contenu. Le menu vient de
 * `GetExperience`, déjà élagué au serveur ; une entrée affichée ne protège rien : la route et
 * l'API restent autorisées au serveur (59 §15). Couleurs, rayons et polices viennent des jetons
 * `color.shell.*` du thème (aucune valeur ici).
 */

type ExperienceContext = ExperienceConfiguration['context'];

export interface AppShellProps {
  navigation: NavigationBody;
  route: Route;
  onNavigate: (route: Route) => void;
  /** Contexte de `GetExperience` : libellés du tenant, de l'entité et de l'identité. */
  context?: ExperienceContext;
  /** Nom affiché venu de la connexion (`/auth/me`) : prioritaire sur le contexte. */
  userLabel?: string;
  /** Nom du produit (`brand.product_name` du thème). */
  productName?: string;
  onSignOut: () => void;
  /** Total d'une requête de badge ; absent : aucun badge. */
  loadBadge?: (query: string) => Promise<number | null>;
  children: ReactNode;
}

const HOVER_OPEN_MS = 120;
const HOVER_CLOSE_MS = 250;

function Badge({ value, label }: { value: number; label: string }) {
  if (value <= 0) return null;
  return (
    <span className="ay-navbadge" aria-label={`${value} ${label}`}>
      {value > 99 ? '99+' : value}
    </span>
  );
}

/** Entrées d'un module dans le tiroir : sections, filets, indentation par profondeur. */
function DrawerEntries({ items, depth, current, badges, onNavigate }: { items: readonly NavItem[]; depth: number; current: Route; badges: ReadonlyMap<string, number>; onNavigate: (route: Route) => void }) {
  return (
    <>
      {items.map((item) => {
        const route = routeOfItem(item);
        const separator = item.separator_before === true ? <li role="separator" className="ay-navdrawer__separator" /> : null;
        if (route === null) {
          return (
            <li key={item.item_id} className="ay-navdrawer__group" role="none">
              {separator !== null && <div role="separator" className="ay-navdrawer__separator" />}
              <div className="ay-navdrawer__section" style={{ paddingInlineStart: `calc(var(--ay-spacing-md) * ${depth + 1})` }}>
                {labelOf(item)}
              </div>
              {(item.children ?? []).length > 0 && (
                <ul className="ay-navdrawer__list" role="none">
                  <DrawerEntries items={item.children!} depth={depth + 1} current={current} badges={badges} onNavigate={onNavigate} />
                </ul>
              )}
            </li>
          );
        }
        const active = sameRoute(route, baseRoute(current));
        return (
          <Fragment key={item.item_id}>
          {separator}
          <li role="none">
            <a
              href={formatRoute(route)}
              className={`ay-navdrawer__link${active ? ' ay-navdrawer__link--active' : ''}`}
              aria-current={active ? 'page' : undefined}
              data-drawer-link=""
              style={{ paddingInlineStart: `calc(var(--ay-spacing-md) * ${depth + 1})` }}
              onClick={(event) => {
                event.preventDefault();
                onNavigate(route);
              }}
            >
              {item.icon !== undefined && <Icon name={item.icon} size={16} />}
              <span className="ay-navdrawer__label">{labelOf(item)}</span>
              {item.badge_query !== undefined && <Badge value={badges.get(item.badge_query) ?? 0} label="élément(s)" />}
            </a>
          </li>
          {item.children !== undefined && item.children.length > 0 && (
            <li role="none">
              <ul className="ay-navdrawer__list" role="none">
                <DrawerEntries items={item.children} depth={depth + 1} current={current} badges={badges} onNavigate={onNavigate} />
              </ul>
            </li>
          )}
          </Fragment>
        );
      })}
    </>
  );
}

/** Arbre déplié (`presentation.layout: tree`) : le menu historique. */
function TreeItems({ items, current, onNavigate, depth, badges }: { items: readonly NavItem[]; current: Route; onNavigate: (route: Route) => void; depth: number; badges: ReadonlyMap<string, number> }) {
  return (
    <ul className={`ay-menu ay-menu--depth-${depth}`} role={depth === 0 ? 'tree' : 'group'}>
      {items.map((item) => {
        const route = routeOfItem(item);
        const active = route !== null && sameRoute(route, baseRoute(current));
        return (
          <li key={item.item_id} role="treeitem" aria-expanded={item.children === undefined ? undefined : true} aria-selected={active} className={item.separator_before === true ? 'ay-menu__item--separated' : undefined}>
            {route === null ? (
              <span className="ay-menu__group">{labelOf(item)}</span>
            ) : (
              <a
                href={formatRoute(route)}
                className={`ay-menu__link${active ? ' ay-menu__link--active' : ''}`}
                aria-current={active ? 'page' : undefined}
                onClick={(event) => {
                  event.preventDefault();
                  onNavigate(route);
                }}
              >
                {item.icon !== undefined && <Icon name={item.icon} size={16} />}
                <span>{labelOf(item)}</span>
                {item.badge_query !== undefined && <Badge value={badges.get(item.badge_query) ?? 0} label="élément(s)" />}
              </a>
            )}
            {item.children !== undefined && item.children.length > 0 && <TreeItems items={item.children} current={current} onNavigate={onNavigate} depth={depth + 1} badges={badges} />}
          </li>
        );
      })}
    </ul>
  );
}

/** Menu mobile : accordéon à deux niveaux (parité v1), rendu seulement ouvert. */
function MobileMenu({ items, current, badges, onNavigate }: { items: readonly NavItem[]; current: Route; badges: ReadonlyMap<string, number>; onNavigate: (route: Route) => void }) {
  const [expanded, setExpanded] = useState<string | null>(() => trailTo(items, current)[0]?.item_id ?? null);
  return (
    <ul className="ay-mobilemenu" role="list">
      {items.map((item) => {
        const route = routeOfItem(item);
        const hasChildren = (item.children ?? []).length > 0;
        if (!hasChildren && route !== null) {
          return (
            <li key={item.item_id}>
              <a
                href={formatRoute(route)}
                className="ay-mobilemenu__link"
                onClick={(event) => {
                  event.preventDefault();
                  onNavigate(route);
                }}
              >
                <Icon name={item.icon} size={18} />
                <span>{labelOf(item)}</span>
                {item.badge_query !== undefined && <Badge value={badges.get(item.badge_query) ?? 0} label="élément(s)" />}
              </a>
            </li>
          );
        }
        const open = expanded === item.item_id;
        return (
          <li key={item.item_id}>
            <button type="button" className="ay-mobilemenu__module" aria-expanded={open} onClick={() => setExpanded(open ? null : item.item_id)}>
              <Icon name={item.icon} size={18} />
              <span>{labelOf(item)}</span>
              <Icon name={open ? 'chevron-down' : 'chevron'} size={16} />
            </button>
            {open && (
              <ul className="ay-navdrawer__list" role="none">
                <DrawerEntries items={route === null ? item.children ?? [] : [{ ...item, children: [] }, ...(item.children ?? [])]} depth={0} current={current} badges={badges} onNavigate={onNavigate} />
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}

interface RailProps {
  items: readonly NavItem[];
  footer: readonly NavItem[];
  current: Route;
  badges: ReadonlyMap<string, number>;
  onNavigate: (route: Route) => void;
  onSignOut: () => void;
}

/**
 * Rail d'icônes (≈ 48 px) et tiroir du module (≈ 256 px), ouvert au survol (délai court), au clic
 * ou au clavier. Rail : liste de boutons à tabulation itinérante (flèches haut/bas, Début, Fin) ;
 * Entrée, Espace ou Flèche droite ouvrent le tiroir et y placent le focus ; Échap ou Flèche gauche
 * le referment et rendent le focus à l'icône.
 */
function Rail({ items, footer, current, badges, onNavigate, onSignOut }: RailProps) {
  const all = useMemo(() => [...items, ...footer], [items, footer]);
  const [open, setOpen] = useState<string | null>(null);
  const [focusIndex, setFocusIndex] = useState(0);
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);
  const drawer = useRef<HTMLDivElement | null>(null);
  const root = useRef<HTMLDivElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeModule = trailTo(all, current)[0]?.item_id;
  const openItem = all.find((item) => item.item_id === open);

  const clearTimer = (): void => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => clearTimer, []);

  const close = useCallback((returnFocus: boolean): void => {
    clearTimer();
    setOpen((previous) => {
      if (returnFocus && previous !== null) {
        const index = all.findIndex((item) => item.item_id === previous);
        queueMicrotask(() => buttons.current[index]?.focus());
      }
      return null;
    });
  }, [all]);

  // Clic hors du rail et du tiroir : fermeture.
  useEffect(() => {
    if (open === null) return;
    const onDown = (event: MouseEvent): void => {
      if (root.current !== null && event.target instanceof Node && !root.current.contains(event.target)) close(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);

  const focusDrawer = (): void => {
    queueMicrotask(() => drawer.current?.querySelector<HTMLElement>('[data-drawer-link]')?.focus());
  };

  const navigate = (route: Route): void => {
    close(false);
    onNavigate(route);
  };

  const activate = (item: NavItem, fromKeyboard: boolean): void => {
    clearTimer();
    const route = routeOfItem(item);
    if ((item.children ?? []).length === 0) {
      if (route !== null) navigate(route);
      return;
    }
    if (open === item.item_id && !fromKeyboard) {
      setOpen(null);
      return;
    }
    setOpen(item.item_id);
    if (fromKeyboard) focusDrawer();
  };

  const moveFocus = (index: number): void => {
    const next = (index + all.length) % all.length;
    setFocusIndex(next);
    buttons.current[next]?.focus();
  };

  const onRailKey = (event: ReactKeyboardEvent<HTMLButtonElement>, index: number, item: NavItem): void => {
    if (event.key === 'ArrowDown') moveFocus(index + 1);
    else if (event.key === 'ArrowUp') moveFocus(index - 1);
    else if (event.key === 'Home') moveFocus(0);
    else if (event.key === 'End') moveFocus(all.length - 1);
    else if (event.key === 'ArrowRight' || event.key === 'Enter' || event.key === ' ') {
      if ((item.children ?? []).length === 0 && event.key === 'ArrowRight') return;
      event.preventDefault();
      activate(item, true);
      return;
    } else if (event.key === 'Escape' || event.key === 'ArrowLeft') close(true);
    else return;
    event.preventDefault();
  };

  const onDrawerKey = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    const links = [...(drawer.current?.querySelectorAll<HTMLElement>('[data-drawer-link]') ?? [])];
    const index = links.indexOf(document.activeElement as HTMLElement);
    if (event.key === 'ArrowDown') links[(index + 1) % links.length]?.focus();
    else if (event.key === 'ArrowUp') links[(index - 1 + links.length) % links.length]?.focus();
    else if (event.key === 'Home') links[0]?.focus();
    else if (event.key === 'End') links[links.length - 1]?.focus();
    else if (event.key === 'Escape' || event.key === 'ArrowLeft') close(true);
    else return;
    event.preventDefault();
  };

  const hoverOpen = (item: NavItem): void => {
    clearTimer();
    if ((item.children ?? []).length === 0) {
      timer.current = setTimeout(() => setOpen(null), HOVER_CLOSE_MS);
      return;
    }
    timer.current = setTimeout(() => setOpen(item.item_id), HOVER_OPEN_MS);
  };
  const hoverLeave = (): void => {
    clearTimer();
    timer.current = setTimeout(() => setOpen(null), HOVER_CLOSE_MS);
  };

  const railButton = (item: NavItem, index: number) => {
    const hasChildren = (item.children ?? []).length > 0;
    const total = badgeTotal(item, badges);
    const isActive = activeModule === item.item_id;
    return (
      <li key={item.item_id} role="none" className={item.separator_before === true ? 'ay-rail__item--separated' : undefined}>
        <button
          type="button"
          ref={(element) => {
            buttons.current[index] = element;
          }}
          className={`ay-rail__button${isActive ? ' ay-rail__button--active' : ''}${open === item.item_id ? ' ay-rail__button--open' : ''}`}
          tabIndex={index === focusIndex ? 0 : -1}
          aria-label={labelOf(item)}
          title={labelOf(item)}
          aria-current={isActive ? 'page' : undefined}
          aria-haspopup={hasChildren ? 'true' : undefined}
          aria-expanded={hasChildren ? open === item.item_id : undefined}
          aria-controls={hasChildren && open === item.item_id ? 'ay-rail-drawer' : undefined}
          onClick={(event) => activate(item, event.detail === 0)}
          onKeyDown={(event) => onRailKey(event, index, item)}
          onFocus={() => setFocusIndex(index)}
          onMouseEnter={() => hoverOpen(item)}
        >
          <Icon name={item.icon} />
          <span className="ay-visually-hidden">{labelOf(item)}</span>
          {total > 0 && <span className="ay-rail__badge" aria-hidden="true">{total > 99 ? '99+' : total}</span>}
        </button>
      </li>
    );
  };

  return (
    <div className="ay-railwrap" ref={root} onMouseLeave={hoverLeave} onMouseEnter={clearTimer}>
      <div className="ay-rail">
        <ul className="ay-rail__list" role="list" aria-label="Modules">
          {items.map((item, index) => railButton(item, index))}
        </ul>
        <span className="ay-rail__spacer" />
        <ul className="ay-rail__list" role="list" aria-label="Organisation">
          {footer.map((item, index) => railButton(item, items.length + index))}
          <li role="none">
            <button type="button" className="ay-rail__button" aria-label="Se déconnecter" title="Se déconnecter" onClick={onSignOut} onMouseEnter={hoverLeave}>
              <Icon name="logout" />
            </button>
          </li>
        </ul>
      </div>
      {openItem !== undefined && (
        <div id="ay-rail-drawer" className="ay-raildrawer" ref={drawer} role="region" aria-label={labelOf(openItem)} onKeyDown={onDrawerKey}>
          <div className="ay-raildrawer__header">
            <h2>{labelOf(openItem)}</h2>
          </div>
          <ul className="ay-navdrawer__list" role="list">
            <DrawerEntries
              items={routeOfItem(openItem) === null ? openItem.children ?? [] : [{ ...openItem, children: [] }, ...(openItem.children ?? [])]}
              depth={0}
              current={current}
              badges={badges}
              onNavigate={navigate}
            />
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Fil d'Ariane sémantique (59 §4.4) : accueil > module > entrée > fiche. La page courante n'y figure
 * pas quand son titre la nomme déjà (liste ouverte sans fiche) : il mène aux parents, sans redite.
 */
function Breadcrumb({ items, route, onNavigate }: { items: readonly NavItem[]; route: Route; onNavigate: (route: Route) => void }) {
  // La médiathèque porte son propre fil d'Ariane : il descend dans ses dossiers (ADR-106).
  if (route.kind === 'home' || route.kind === 'media') return null;
  const trail = trailTo(items, route);
  const crumbs: Array<{ key: string; label: string; route: Route | null }> = [{ key: 'home', label: 'Accueil', route: { kind: 'home' } }];
  const current = !hasRecord(route) && route.kind !== 'task';
  trail.forEach((item, index) => {
    if (current && index === trail.length - 1 && (route.kind === 'view' || route.kind === 'object')) return;
    const own = routeOfItem(item);
    crumbs.push({ key: item.item_id, label: labelOf(item), route: index === trail.length - 1 && !hasRecord(route) && route.kind !== 'task' ? null : own });
  });
  if (trail.length === 0) {
    const fallback = route.kind === 'account' ? 'Sécurité du compte' : route.kind === 'tenant' ? 'Tenant' : route.kind === 'attention' ? 'Attention' : route.kind === 'inbox' ? 'Mon travail' : route.kind === 'graph' ? 'Graphe' : route.kind === 'model' ? 'Modèle' : route.kind === 'forms' ? 'Formulaires' : route.kind === 'views' ? 'Vues' : null;
    if (fallback !== null) crumbs.push({ key: route.kind, label: fallback, route: null });
  }
  if (hasRecord(route)) crumbs.push({ key: 'record', label: 'Fiche', route: null });
  if (route.kind === 'task') crumbs.push({ key: 'task', label: 'Tâche', route: null });
  if (crumbs.length < 2) return null;
  return (
    <nav className="ay-breadcrumb" aria-label="Fil d’Ariane">
      <ol>
        {crumbs.map((crumb, index) => (
          <li key={crumb.key}>
            {crumb.route === null || index === crumbs.length - 1 ? (
              <span aria-current={index === crumbs.length - 1 ? 'page' : undefined}>{crumb.label}</span>
            ) : (
              <a
                href={formatRoute(crumb.route)}
                onClick={(event) => {
                  event.preventDefault();
                  onNavigate(crumb.route!);
                }}
              >
                {crumb.label}
              </a>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function hasRecord(route: Route): boolean {
  return (route.kind === 'view' || route.kind === 'object') && route.record_id !== undefined;
}

export function AppShell({ navigation, route, onNavigate, context, userLabel, productName, onSignOut, loadBadge, children, bare = false }: AppShellProps & { bare?: boolean }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const layout = navigation.presentation?.layout ?? 'rail';
  const footer = navigation.footer_items ?? [];
  const everything = useMemo(() => [...navigation.items, ...footer, ...(navigation.user_menu_items ?? [])], [navigation, footer]);
  const queries = useMemo(() => badgeQueries(everything), [everything]);
  // L'état d'une liste (ADR-094) change l'URL sans changer de page : il ne relance pas les badges.
  const badges = useBadges(queries, loadBadge, formatRoute(route).split('?')[0]!);
  const tenantLabel = context?.tenant_label ?? text(navigation.label, 'fr', 'AYUMIA');
  const [density, setDensity] = useState<Density>(readDensity);
  const chooseDensity = (next: Density): void => {
    setDensity(next);
    writeDensity(next);
  };
  const [panel, setPanel] = useState<TopbarPanel | null>(null);
  const closePanel = useCallback(() => setPanel(null), []);
  const name = userLabel ?? context?.identity_display_name ?? 'Mon compte';
  const navigate = (next: Route): void => {
    setMenuOpen(false);
    onNavigate(next);
  };

  return (
    // `bare` : page plein écran (éditeur d'un studio, app/full-screen.ts). Les éléments de la coquille ne sont pas
    // rendus, mais chaque emplacement reste à sa place : la page n'est jamais remontée en changeant de mode.
    <div className={`ay-shell ay-shell--${layout}${menuOpen ? ' ay-shell--menu-open' : ''}${panel === null ? '' : ' ay-shell--panel-open'}${bare ? ' ay-shell--bare' : ''}`} data-density={density}>
      <a className="ay-skip-link" href="#ay-main">
        Aller au contenu
      </a>
      {!bare && <Topbar
        context={context}
        tenantLabel={tenantLabel}
        productName={productName ?? 'AYUMIA'}
        name={name}
        userMenuItems={navigation.user_menu_items ?? []}
        density={density}
        onDensity={chooseDensity}
        panel={panel}
        onPanel={setPanel}
        menuOpen={menuOpen}
        onMenu={() => setMenuOpen(!menuOpen)}
        onNavigate={navigate}
        onSignOut={onSignOut}
      />}
      {!bare && <nav id="ay-sidemenu" className="ay-sidemenu" aria-label="Menu principal">
        {layout === 'tree' ? (
          <div className="ay-tree">
            <TreeItems items={navigation.items} current={route} onNavigate={navigate} depth={0} badges={badges} />
            {footer.length > 0 && <TreeItems items={footer} current={route} onNavigate={navigate} depth={0} badges={badges} />}
            <button type="button" className="ay-button ay-button--secondary ay-tree__signout" onClick={onSignOut}>
              <Icon name="logout" size={16} /> Se déconnecter
            </button>
          </div>
        ) : (
          <Rail items={navigation.items} footer={footer} current={route} badges={badges} onNavigate={navigate} onSignOut={onSignOut} />
        )}
        {menuOpen && (
          <div className="ay-mobilepanel">
            <MobileMenu items={[...navigation.items, ...footer]} current={route} badges={badges} onNavigate={navigate} />
          </div>
        )}
      </nav>}
      {menuOpen && <div className="ay-shell__scrim" onClick={() => setMenuOpen(false)} />}
      <main id="ay-main" className="ay-main" tabIndex={-1}>
        {!bare && <div className="ay-watermark" aria-hidden="true">
          <Monogram label={tenantLabel} />
        </div>}
        <div className="ay-main__content">
          {!bare && <OfflineBanner />}
          {!bare && <Breadcrumb items={[...navigation.items, ...footer]} route={route} onNavigate={navigate} />}
          {children}
        </div>
      </main>
      {panel !== null && <TopbarSidePanel panel={panel} onClose={closePanel} />}
    </div>
  );
}
